import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  withTextLocalizer,
  setPhoneNumber as setPhoneNumberRedux,
  LocalizePropType,
  selectPhoneCodes,
  forgetPassword,
  setNotification,
  APP,
  validatePhoneNumber,
} from 'getaway-data-layer';
import ForgetEmailView from './ForgetEmailView';
import { navigateTo } from '../../../utils';
import { PhoneItem } from './types';
import { useQuery } from '../../../hooks/useQuery';

type Props = {
  localize: LocalizePropType;
};

const ForgetEmailContainer = ({ localize }: Props) => {
  const history = useHistory();
  const query = useQuery();
  const dispatch = useDispatch();
  const phoneCodes = useSelector(selectPhoneCodes);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const [turnstileWidgetId, setTurnstileWidgetId] = useState<string | null>(
    null
  );
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const [phoneNumber, setPhoneNumber] = useState<PhoneItem>({
    value: '',
    dialCode: null,
    error: '',
  });

  const phoneNumberQuery = query.get('phoneNumber');

  const turnstileCheck = () => {
    // If the widget already exists, just reset it to generate a new token.
    if (turnstileWidgetId) {
      window.turnstile.reset(turnstileWidgetId);
      return;
    }

    const widgetId = window.turnstile.render(turnstileRef.current, {
      sitekey: process.env.VITE_TURNSTILE_SITE_KEY,
      callback: (token: string) => {
        setTurnstileToken(token);
      },
      theme: 'light',
      retry: 'never',
    });

    setTurnstileWidgetId(widgetId);
  };

  const onSubmit = () => {
    if (isValidNumber()) {
      dispatch(setPhoneNumberRedux(phoneNumber.value));
      if (
        process.env.VITE_TURNSTILE_SITE_KEY &&
        window.turnstile &&
        window.location.hostname !== 'localhost'
      ) {
        turnstileCheck();
      } else {
        dispatch(
          setNotification({
            message: 'backend.error',
            type: APP.NOTIFICATION_TYPE.ERROR,
          })
        );
      }
    }
  };

  const onPhoneNumberChanged = (value: string, data: any) => {
    let newVal = value.startsWith(data?.dialCode)
      ? value.slice(data?.dialCode?.length)
      : value;
    const zeroRegex = new RegExp(`^[0]*`, 'gi');
    newVal = newVal.replace(zeroRegex, '');
    const dialCodeRegex = new RegExp(`^${data?.dialCode}`, 'gi');
    newVal = newVal.replace(dialCodeRegex, '');
    newVal = `+${data?.dialCode}${newVal}`;

    setPhoneNumber({
      dialCode: data?.dialCode,
      value: newVal,
      error: '',
    });
  };

  const isValidNumber = () => {
    const error = validatePhoneNumber(
      phoneNumber.value?.slice(phoneNumber.dialCode?.length)
    );
    setPhoneNumber({
      dialCode: phoneNumber.dialCode,
      value: phoneNumber.value,
      error,
    });
    return !error;
  };

  const onClose = () => {
    navigateTo(history, '/forgot');
  };

  useEffect(() => {
    if (turnstileToken) {
      dispatch(
        forgetPassword(
          null,
          phoneNumber.value,
          turnstileToken,
          localize,
          () => {
            navigateTo(history, '/verify-phone');
          },
          () => {
            setTurnstileToken(null);
          }
        )
      );
    }
  }, [turnstileToken]);

  useEffect(() => {
    if (phoneNumberQuery && phoneCodes?.length > 0) {
      // Find the matching country code
      const matchedCode = phoneCodes?.find((code) =>
        phoneNumberQuery.startsWith(code.code)
      );

      if (matchedCode) {
        const cleanedPhoneNumber = phoneNumberQuery.startsWith('+')
          ? phoneNumberQuery.slice(1)
          : phoneNumberQuery;

        const dialCodeNumber = matchedCode.code.replace('+', '');

        onPhoneNumberChanged(cleanedPhoneNumber, {
          dialCode: dialCodeNumber,
        });
      }
    }
  }, [phoneNumberQuery, phoneCodes]);

  useEffect(() => {
    if (
      phoneNumberQuery &&
      phoneNumber.value &&
      phoneNumberQuery === phoneNumber.value
    ) {
      onSubmit();
    }
  }, [phoneNumberQuery, phoneNumber.value]);

  return (
    <ForgetEmailView
      phoneNumber={phoneNumber}
      localize={localize}
      phoneCodes={phoneCodes}
      onClose={onClose}
      onSubmit={onSubmit}
      isValidNumber={isValidNumber}
      onPhoneNumberChanged={onPhoneNumberChanged}
      turnstileRef={turnstileRef}
    />
  );
};

export default withTextLocalizer(ForgetEmailContainer);
